import { useEffect, useMemo, useRef, useState } from 'react';
import * as atlas from 'azure-maps-control';
import 'azure-maps-control/dist/atlas.min.css';
import { SvgIcon } from '@progress/kendo-react-common';
import { useLocalization } from '@progress/kendo-react-intl';
import { currentLocation } from '../assets/current-location'
import { renderToStaticMarkup } from 'react-dom/server';
import { loadingPanel } from '../components/LoadingPanel';


export const AzureMapComponent = (props: any) => {
  const mapContainer = useRef(null);
  const mapRef = useRef<atlas.Map | null>(null);
  const groupMarkersRef = useRef<atlas.HtmlMarker[]>([]);
  const beaconMarkersRef = useRef<atlas.HtmlMarker[]>([]);
  const alarmMarkersRef = useRef<any>([]);

  const localizationService = useLocalization();
  const [markerLayer, setMarkerLayer] = useState<atlas.source.DataSource | null>(null);  
  const [mapReady, setMapReady] = useState(false);
  const [markerInit, setMarkerInit] = useState(false);

  const [loading, setLoading] = useState(false);


  const {
    onlyBound,
    alarmInfo,
    alarmToggle,
    geoStaticToggle,
    map,
    devices: devices,
    beacons,
    deviceMac,
    zones,
    zone,
    subscription,
    zoom: propZoom,
    center: propCenter
  } = props;

  let currentMap:any = map;
  //if (!currentMap?.length && currentMap != null) {
  if (currentMap == null) {
    try {
      currentMap = JSON.parse(localStorage.getItem('mapSelected') || '{}');
    } catch {
      currentMap = {};
    }
  }
  const zonesList = subscription ? zone : zones;

  const defaultLatitude:number = (currentMap?.botLeft?.latitude + currentMap?.topLeft?.latitude) / 2;
  const defaultLongitude:number = (currentMap?.botLeft?.longitude + currentMap?.botRight?.longitude) / 2;

  const [center, setCenter] = useState<[number, number]>([defaultLongitude, defaultLatitude]);
  const [zoom, setZoom] = useState<number>(propZoom || 20);
  const [mapStyle, setMapStyle] = useState<string>(() => {
    const savedStyle = localStorage.getItem('mapStyle');
    return savedStyle || 'satellite';
  });

  const boundingBox = useMemo(() => {    
    const map:any = currentMap;
    return [
      map?.botLeft?.longitude,
      map?.botLeft?.latitude,
      map?.topRight?.longitude,
      map?.topRight?.latitude
    ];
  }, [currentMap]);

  const getSelectedZones = () => {
    const zonesToProcess = props.subscription ? [zonesList] : zonesList || [];
    const selectedZone: any[] = [];
    const zonesLatLngList: any[] = [];
  
    zonesToProcess.forEach((zone: any) => {
      if (zone?.id !== 0) {
        const zoneColor = typeof zone.color === 'string' && zone.color.trim() ? zone.color : 'red';
        const coordinates = zone.points?.map((point: any) => [point.longitude, point.latitude]) || [];

        // Se ci sono coordinate, aggiungiamo la prima alla fine
        if (coordinates.length > 0) {
          coordinates.push(coordinates[0]);
        }
  
        const newZone = {
          type: 'Feature',
          properties: { fillColor: zoneColor },
          geometry: {
            type: 'Polygon',
            coordinates: [coordinates],
          },
        };
  
        selectedZone.push(newZone);
        zonesLatLngList.push(coordinates);
      }
    });
  
    return { selectedZone, zonesLatLngList };
  };  

  const validBoundingBox = Array.isArray(boundingBox) && boundingBox.length === 2 && 
    boundingBox.every(coord => Array.isArray(coord) && coord.length === 2);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    setMapReady(false);

    const onMapReady = () => {
      setMapReady(true);
    };

    map.events.add('ready', onMapReady);

    return () => {
      map?.events.remove('ready', onMapReady);
    };
  }, [mapRef.current]); 

  useEffect(() => {
    if (!mapContainer.current) return;

    const oldMap = mapRef.current;

    if (oldMap) {
      const currentStyle:any = oldMap.getStyle().style;
      setMapStyle(currentStyle);
    }
    
    setMapReady(false);

    const map = new atlas.Map(mapContainer.current, {
      style: 'satellite',
      center,
      zoom,
      minZoom: 5,
      maxZoom: 20,
      maxBounds: validBoundingBox ? boundingBox : undefined,
      language: 'it-IT',
      authOptions: {
        authType: atlas.AuthenticationType.subscriptionKey,
        subscriptionKey: process.env.REACT_APP_AZURE_MAPS_KEY,
      },
      attributionControl: false,
    });

    mapRef.current = map;

     // Salva lo stile quando l'utente lo cambia
    map.events.add('stylechanged', () => {
      const currentStyle:any = map.getStyle().style;
      setMapStyle(currentStyle);
      localStorage.setItem('mapStyle', currentStyle); // Salva lo stile in localStorage
    });

    map.controls.add([new atlas.control.ZoomControl(), new atlas.control.FullscreenControl()], {
      position: atlas.ControlPosition.TopRight
    });

    map.controls.add(new atlas.control.StyleControl({
      mapStyles: ['satellite', 'road'],
      layout: 'icons'
    }), {
      position: atlas.ControlPosition.TopRight
    });

    map.controls.add(new atlas.control.ScaleControl({ unit: 'metric' }), {
      position: atlas.ControlPosition.TopRight
    });

    //map.events.add('zoom',  () => {
    //  const zoom = map.getCamera().zoom;
    //  console.log('Zoom attuale:', zoom);
    //});
   
    resetZoom()

    map.events.add('ready', () => {
      const source = new atlas.source.DataSource();
      map.sources.add(source);
      setMarkerLayer(source);
      setMapReady(true);
    });

    return () => map?.dispose();
  }, [map, boundingBox, center]);

  //useEffect(() => {
  //  if (mapReady && groups && groups.length > 0) {
  //    setMarkerInit(true);
  //  }
  //}, [mapReady, groups]);

  // Sync map camera with props
  useEffect(() => {
    const map = mapRef.current;
    if (map && mapReady) {
      if (propZoom !== undefined) {
        setZoom(propZoom);
        map.setCamera({ zoom: propZoom });        
      }
      //if (propCenter !== undefined) {
      //  setCenter(propCenter);
      //  map.setCamera({ center: propCenter });
      //}else{
      //}
      if (propCenter !== undefined) {
        setCenter(propCenter);
        map.setCamera({ center: propCenter });
      }
    }
  }, [propZoom, propCenter, mapReady]);

  //manage zones
  useEffect(() => {
      if (!mapReady) return;

      const map = mapRef.current;
      if (!map || !zonesList || !mapReady || !mapContainer.current || map.sources === undefined) return;
  
      const zoneLayerId = 'zone-polygon-layer';
      const zoneSourceId = 'zone-source';
  
      const existingLayer = map.layers.getLayerById(zoneLayerId);
      if (existingLayer) {
        map.layers.remove(existingLayer);
      }
  
      const existingSource = map.sources.getById(zoneSourceId);
      if (existingSource) {
        map.sources.remove(existingSource);
      }
  
      if(zonesList.length == 0) return
      const { selectedZone } = getSelectedZones();
  
      const dataSource:any = new atlas.source.DataSource(zoneSourceId);
      map?.sources?.add(dataSource); // <-- qui evitato errore
  
      dataSource?.add({
        type: 'FeatureCollection',
        features: selectedZone
      });
  
      const polygonLayer = new atlas.layer.PolygonLayer(dataSource, zoneLayerId, {
        fillColor: ['coalesce', ['get', 'fillColor'], 'transparent'],
        fillOpacity: 0.5,
      });
  
      map.layers.add(polygonLayer);
  
  }, [zonesList, mapReady]);
  
  //geoStaticToggle
  useEffect(() => {
    if (!mapReady) return;
    
    const map = mapRef.current;
    if (!map) return;
  
    // Rimuove i marker dei gruppi
    //groupMarkersRef.current.forEach(marker => map.markers.remove(marker));
    //groupMarkersRef.current = [];
  
    // Rimuove i marker dei beacon
    beaconMarkersRef.current.forEach(marker => map.markers.remove(marker));
    beaconMarkersRef.current = [];
  
    // Rimuove le zone (layer + source)
    const zoneLayer = map.layers.getLayerById('zone-polygon-layer');
    if (zoneLayer) {
      map.layers.remove(zoneLayer);
    }
  
    const zoneSource = map.sources.getById('zone-source');
    if (zoneSource) {
      map.sources.remove(zoneSource);
    }
  
    // Stato zone selezionate
  }, [geoStaticToggle]);

  //moveend
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const handleMove = () => {
      const camera:any = map.getCamera();
      setZoom(camera.zoom);
      setCenter(camera.center);
    };

    map.events.add('moveend', handleMove);
    return () => map.events.remove('moveend', handleMove);
  }, []);

  //markers
  useEffect(() => {
    setLoading(true);
    if (!mapReady || !devices || !markerLayer) return;
    
    const map:any = mapRef.current;    
    if(!map) return

    markerLayer.clear();
    
    // 1. Rimuovi i marker dei gruppi precedentemente aggiunti
    groupMarkersRef.current.forEach(marker => {
      map.markers.remove(marker);
    });
    // 2. Pulisci il riferimento
    groupMarkersRef.current = [];

    // 1. Rimuovi i marker dei beacon precedentemente aggiunti
    beaconMarkersRef.current.forEach(marker => {
      map.markers.remove(marker);
    });
    // 2. Pulisci il riferimento
    beaconMarkersRef.current = [];

    // 1. Rimuovi il marker dell'allarme precedente (se esiste)
    if (alarmMarkersRef.current) {
      map.markers.remove(alarmMarkersRef.current);
    }
    // 2. Pulisci il riferimento dell'allarme
    alarmMarkersRef.current = null;

    const popup = new atlas.Popup({ pixelOffset: [0, -18], closeButton: false });

    const addTooltipEvents = () => {
      document.querySelectorAll('.k-icon').forEach((el) => {
        el.addEventListener('mouseenter', (e: any) => {
          const span = e.currentTarget;
          const name = span?.getAttribute('data-name');
          const mac = span?.getAttribute('data-mac');
          const type = span?.getAttribute('data-type');
          const beaconType = span?.getAttribute('data-beacon-type');
          const datetime = span?.getAttribute('data-datetime');

          let html = '<div style="background:white;padding:5px;border-radius:5px;text-align:center">';
          if (type === 'group') {
            html += `<b>${localizationService.toLanguageString('azure.name', 'Name')}: ${name}</b><br><b>Mac: ${mac}</b>`;
          } else if (type === 'beacon') {
            html += `<b>${localizationService.toLanguageString('azure.name', 'Name')}: ${name}</b><br><b>Type: ${beaconType}</b>`;
          } else if (type === 'alarm') {
            html += `<b>${localizationService.toLanguageString('azure.device_mac', 'Device MAC')}: ${mac}</b><br><b>${localizationService.toLanguageString('azure.dateTime', 'DateTime')}: ${datetime}</b>`;
          }
          html += '</div>';

          //popup.setOptions({ content: html, position: markerRef[span.getAttribute('data-key')] });
          //popup.open(map);
          const position = markerRef[span.getAttribute('data-key')];
          if (position) {
            popup.setOptions({ content: html, position: position });
            popup.open(map);
          }
        });
        el.addEventListener('mouseleave', () => popup.close());
      });
    };

    const markerRef: { [key: string]: any } = {};

    //groups

    /* CLUSTER GROUPS */
    groupMarkersRef.current.forEach(marker => map.markers.remove(marker));
    groupMarkersRef.current = [];

    // Rimuovi layer/sources precedenti
    map.layers.getLayers().forEach((layer:any) => {
      if (layer.id.startsWith("group")) {
        map.layers.remove(layer);
      }
    });
    
    const groupSourceId = 'group-source';
    // 1. Rimuovi tutti i layer collegati alla source 'group-source'
    map.layers.getLayers().forEach((layer: any) => {
      const source = layer.getSource?.();
      if (source && source.getId && source.getId() === groupSourceId) {
        map.layers.remove(layer);
      }
    });

    // 2. Ora è sicuro rimuovere la source
    const existingGroupSource = map.sources.getById(groupSourceId);
    if (existingGroupSource) {
      map.sources.remove(existingGroupSource);
    }

    // Crea DataSource con clustering
    const groupSource = new atlas.source.DataSource('group-source', {
      cluster: true,
      clusterRadius: 60,
      //clusterMaxZoom: 17
    });
       
    // Aggiungi gruppi come FeatureCollection
    (Array.isArray(devices) ? devices : JSON.parse(devices || '[]'))?.forEach((group: any, i: number) => {
      if (onlyBound && !group?.name){
        groupSource?.clear();
        groupSource?.add([]);
        return;
      } 
      if (deviceMac && group.mac !== deviceMac){
        groupSource?.clear();
        groupSource?.add([]);
        return;
      }

      const groupFeatures:any = {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [group?.position?.longitude ?? 0, group?.position?.latitude ?? 0],
        },
        properties: {
          name: group?.name,
          mac: group?.mac,
          color: group?.color || group?.groupColor || '#187D88',
          type: 'group',
          id: `group-${i}`,
        }
      };
      groupSource?.add(groupFeatures);
    });

    ///***  Cluster counts */
    const getSvgImage = (color?: string) => {
      const svg = renderToStaticMarkup(
        <svg viewBox="0 0 24 24" fill={color} width="36" height="36" stroke="white" strokeWidth="1" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2C8.13 2 5 5.13 5 9c0 4.5 6 11 7 11s7-6.5 7-11c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
        </svg>
      );
      return `data:image/svg+xml;base64,${btoa(svg)}`;
    };
    
    const loadSvgImage = (iconId: string, color: string) => {
      return new Promise<void>((resolve) => {
        const img = new Image();
        img.onload = () => {
          map.imageSprite.add(iconId, img);
          resolve();
        };
        img.src = getSvgImage(color);
      });
    };

    const registeredIcons = new Set<string>();

    const parsedDevices = Array.isArray(devices) ? devices : JSON.parse(devices || '[]');
    groupSource?.clear();

    const loadAllImages: Promise<void>[] = [];
    
    parsedDevices?.forEach((device: any) => {
    
      if ((onlyBound && !device?.name) || (deviceMac && (device.mac !== deviceMac && device.deviceMac !== deviceMac))) {
        return;
      }

      const color = device?.color || device?.groupColor || '#187D88';
      const colorHex = color.toUpperCase(); // Consistenza
      const iconId = `marker-icon-${colorHex}`;

      if (!registeredIcons.has(iconId)) {
        registeredIcons.add(iconId);
        loadAllImages.push(loadSvgImage(iconId, colorHex));
      }

      const feature = {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [
            device?.position?.longitude ?? 0,
            device?.position?.latitude ?? 0,
          ],
        },
        properties: {
          name: device?.name,
          mac: device?.mac,
          color: colorHex,
          icon: iconId,
        },
      };

      groupSource.add(feature);
    });

    Promise.all(loadAllImages).then(() => {
      // Solo quando tutte le immagini sono caricate

      const ensureLayerReady = () => {
        if (map?.ready) {
          if (!map.sources.getById('group-source')) {
            map.sources.add(groupSource);
          }
        } else {
          map.events.addOnce('ready', map, () => {
            if (!map.sources.getById('group-source')) {
              map.sources.add(groupSource);
            }
          });
        }

        const unclusteredLayer = new atlas.layer.SymbolLayer(groupSource, 'group-unclustered-symbol', {
          filter: ['!', ['has', 'point_count']],
          iconOptions: {
            image: ['get', 'icon'],
            allowOverlap: true,
            size: 1,
            anchor: 'center',
            padding: 1
          },
        });

        const clusterCount = new atlas.layer.SymbolLayer(groupSource, 'group-cluster-count', {
          filter: ['has', 'point_count'],
          textOptions: {
            textField: ['get', 'point_count_abbreviated'],
            offset: [0, 0],
            allowOverlap: true,
            ignorePlacement: true,
            haloColor: 'white',
            haloWidth: 2
          }
        });

        const addLayers = () => {
          const existingLayers = map.layers.getLayers();

          if (!existingLayers.find((l: any) => l.getId() === 'group-unclustered-symbol')) {
            map.layers.add(unclusteredLayer);
          }

          if (!existingLayers.find((l: any) => l.getId() === 'group-cluster-count')) {
            map.layers.add(clusterCount);
          }

          // Eventi mouseover/mouseout solo una volta
          setTimeout(() => {
            map.events.add('mouseover', unclusteredLayer, (e: any) => {
              const props = e?.shapes[0]?.getProperties();
              const position = e?.shapes[0]?.getCoordinates?.() ?? e?.shapes[0]?.geometry?.coordinates;
              const offset = new atlas.Pixel(0, -40);
              const html = `
                <div style="background:white;padding:5px;border-radius:5px;text-align:center;">
                  <b>${localizationService.toLanguageString('azure.name', 'Name')}:  ${props?.name == null ? '' : props?.name}</b><br>
                  <b>MAC: ${props?.mac}</b>
                </div>`;
              popup.setOptions({ content: html, position, offset });
              popup.open(map);
              map.getCanvas().style.cursor = 'pointer';
            });

            map.events.add('mouseout', unclusteredLayer, () => {
              popup.close();
            });
          }, 0);
        };

        if (map?.ready) {
          addLayers();
        } else {
          map.events.addOnce('ready', addLayers);
        }
      };


      if (map?.ready) {
        ensureLayerReady();
      } else {
        map.events.addOnce('ready', ensureLayerReady);
      }      
    });

    //beacons
    beacons?.filter((b: any) => b?.checked).forEach((beacon: any, i: number) => {
      const color = beacon?.color || 'gray';
      const marker = new atlas.HtmlMarker({
        position: [beacon?.longitude ?? 0, beacon?.latitude ?? 0],
        htmlContent: `<span class="k-icon" style="cursor: pointer; font-size:32px;" data-name="${beacon?.name}" data-type="beacon" data-beacon-type="${beacon?.type}" data-key="beacon-${i}">
        <svg viewBox="0 0 24 24" fill=${color} width="48" height="48" stroke="white" stroke-width="1">
        <path d="M6 6h12v12H6z" />
        </svg>
        </span>`
      });
      markerRef[`beacon-${i}`] = marker.getOptions().position;
      map.markers.add(marker);
      
      beaconMarkersRef?.current?.push(marker);
    });

    //alarms
    if (alarmToggle && alarmInfo) {
      const localDate = new Date(alarmInfo?.dateTime); // Parses as UTC
      // Convert to local date-time string
      const dateTime = localDate.toLocaleString(); // Uses system locale
      const marker = new atlas.HtmlMarker({
        position: [alarmInfo?.info?.position?.longitude ?? 0, alarmInfo?.info?.position?.latitude ?? 0],
        htmlContent: `<span class="k-icon" style="cursor: pointer;font-size:32px;" data-mac="${alarmInfo?.deviceMac}" data-type="alarm" data-datetime="${dateTime}" data-key="alarm">
          <svg viewBox="0 0 24 24" fill="red" width="48" height="48" stroke="white" stroke-width="1">
            <path d="M12 2L1 21h22L12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
          </svg>
        </span>`
      });
      markerRef[`alarm`] = marker.getOptions().position;
      map.markers.add(marker);
      // Memorizza il marker dell'allarme
      alarmMarkersRef.current = marker;
    }

    addTooltipEvents();
    setLoading(false);
  }, [devices, beacons, alarmInfo, alarmToggle, onlyBound, deviceMac, markerLayer, mapReady]);
  
  const resetZoom = () => {
    if (mapRef?.current) {
      mapRef?.current?.setCamera({
        center: [defaultLongitude, defaultLatitude],
        zoom: 15.75,
        maxBounds: boundingBox,
      });
    }
  };
  
  return (
    <div style={{ position: 'relative', height: '100%', width: '100%' }}>
      {/*{loadingPanel && loading}*/}
      <button
        className='azure-reset-zoom'
        title={localizationService.toLanguageString('azure.reset_zoom', 'NOT_FOUND')}
        onClick={resetZoom}
      >
        <SvgIcon icon={currentLocation}  className="icon"/>
      </button>
      <div ref={mapContainer} style={{ height: '100%', width: '100%' }} />
    </div>
  );
  
};

